import { useEffect, useRef, useState, useCallback } from 'react';

// csTimer 风格双阶段计时器：
// idle →（按住）ready(红) →（松开且按住≥500ms）green(绿，可开始) →（按下）running →（按下）stopped
// 移动端：长按触屏代替空格
export const HOLD_MS = 500;

export function useTimer() {
  const [state, setState] = useState('idle'); // idle | ready | green | running | stopped
  const [displayMs, setDisplayMs] = useState(0);
  const [resultMs, setResultMs] = useState(null);
  const ref = useRef({ holdStart: 0, runStart: 0, raf: 0, state: 'idle' });

  const cancelRaf = useCallback(() => {
    if (ref.current.raf) cancelAnimationFrame(ref.current.raf);
    ref.current.raf = 0;
  }, []);

  const setInternal = useCallback((s) => {
    ref.current.state = s;
    setState(s);
  }, []);

  const loop = useCallback(() => {
    setDisplayMs(performance.now() - ref.current.runStart);
    ref.current.raf = requestAnimationFrame(loop);
  }, []);

  const start = useCallback(() => {
    ref.current.runStart = performance.now();
    setDisplayMs(0);
    cancelRaf();
    setInternal('running');
    ref.current.raf = requestAnimationFrame(loop);
  }, [cancelRaf, loop, setInternal]);

  const stop = useCallback(() => {
    const ms = performance.now() - ref.current.runStart;
    cancelRaf();
    setDisplayMs(ms);
    setResultMs(ms);
    setInternal('stopped');
  }, [cancelRaf, setInternal]);

  const reset = useCallback(() => {
    cancelRaf();
    setDisplayMs(0);
    setResultMs(null);
    setInternal('idle');
  }, [cancelRaf, setInternal]);

  const press = useCallback(() => {
    const s = ref.current.state;
    if (s === 'idle') {
      ref.current.holdStart = performance.now();
      setInternal('ready');
    } else if (s === 'green') {
      start();
    } else if (s === 'running') {
      stop();
    }
    // stopped 状态下的空格/点击不动作，避免误触丢弃成绩；请用按钮选择结果或“重试”
  }, [setInternal, start, stop, reset]);

  const release = useCallback(() => {
    const s = ref.current.state;
    if (s === 'ready') {
      if (performance.now() - ref.current.holdStart >= HOLD_MS) {
        setInternal('green');
      } else {
        setInternal('idle');
      }
    }
    // stopped 状态不自动回到 idle，等待用户选择罚时或重试
  }, [setInternal]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        if (!e.repeat) press();
      }
    };
    const onKeyUp = (e) => {
      if (e.code === 'Space') release();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      cancelRaf();
    };
  }, [press, release, cancelRaf]);

  return { state, displayMs, resultMs, press, release, reset };
}
